<script setup lang="ts">
import { computed } from 'vue'
import type { IProgramSuccessResponse } from '@/types/cpm/programSuccess'
import { formatCurrency, formatMegawatts, formatPercentage } from '@/util/formatters'

/**
 * Headline KPI cards for the TFA program-success dashboard: success ratio, savings,
 * average dispatched hours per month, average load, and average downtime.
 *
 * @prop data    - The program-success response; metrics render as placeholders until it loads.
 * @prop loading - Shows the loading overlay while the query is in flight.
 */
const props = defineProps<{ data?: IProgramSuccessResponse; loading?: boolean }>()

/** Rate at or above which the headline reads as healthy and turns green. */
const SUCCESS_RATE_TARGET_PCT = 75

/** "X / Y" headline, or "N/A" when the denominator is 0. */
const successRatioDisplay = computed(() => {
  const ratio = props.data?.success_ratio
  if (!ratio || ratio.denominator === 0) return 'N/A'
  return `${ratio.numerator}/${ratio.denominator}`
})

/**
 * Green only once the site clears the target — a flat green read as an endorsement
 * even on an all-fail range. Below target the number stays the default text colour
 * (the bar chart already carries the per-month red/green detail); muted when there's
 * nothing to judge, i.e. no data yet or every month undetermined, which is when the
 * backend nulls the rate.
 */
const successRatioClass = computed(() => {
  const rate = props.data?.success_metrics.success_rate_pct
  if (rate == null) return 'text-text-tertiary'
  return rate >= SUCCESS_RATE_TARGET_PCT ? 'text-emerald-500' : ''
})

/** Avg dispatched hours per month, rounded to the nearest whole hour. */
const hoursPerMonthDisplay = computed(() => (props.data ? String(Math.round(props.data.avg_dispatched_hours)) : '—'))

/**
 * Average load over the interval, in MW. "N/A" when the backend has no savings
 * record for the range — it nulls this alongside `potential_savings_usd`, so this
 * tile and the savings tile blank out together.
 */
const avgLoadDisplay = computed(() => {
  if (!props.data) return '—'
  const load = props.data.success_metrics.avg_load_mw
  if (load == null) return 'N/A'
  return formatMegawatts(load)
})

/** Gates the MW unit, so it isn't appended to a "—" or "N/A". */
const hasAvgLoad = computed(() => props.data?.success_metrics.avg_load_mw != null)

/** Average monthly downtime as a percentage. */
const avgDowntimeDisplay = computed(() => (props.data ? formatPercentage(props.data.avg_downtime_pct) : '—'))

/**
 * Savings over the interval. The template renders the "$" as a muted prefix, so the
 * formatter leaves the symbol off; "N/A" when the backend has no savings record for
 * the range, which is distinct from a genuine $0.
 */
const potentialSavingsDisplay = computed(() => {
  if (!props.data) return '—'
  const savings = props.data.success_metrics.potential_savings_usd
  if (savings == null) return 'N/A'
  return formatCurrency(savings, 0, false)
})
</script>

<template>
  <div v-loading="loading" class="h-full flex flex-col justify-between gap-4">
    <span class="uppercase text-xs text-text-tertiary">CPM Program Success</span>

    <div class="grid grid-cols-3 items-end gap-8">
      <div class="flex flex-col gap-1">
        <span class="text-3xl font-semibold" :class="successRatioClass">{{ successRatioDisplay }}</span>
        <span class="text-sm text-text-tertiary">Success Rate</span>
      </div>

      <div class="flex flex-col gap-1 col-span-2">
        <span class="text-2xl font-semibold">
          <span v-if="props.data?.success_metrics.potential_savings_usd" class="text-text-tertiary pr-1">$</span
          >{{ potentialSavingsDisplay }}</span
        >
        <span class="text-sm text-text-tertiary">Potential savings</span>
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-2xl font-semibold">{{ hoursPerMonthDisplay }}</span>
        <span class="text-sm text-text-tertiary">hrs/mo avg</span>
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-2xl font-semibold"
          >{{ avgLoadDisplay }}<span v-if="hasAvgLoad" class="text-text-tertiary pl-1">MW</span></span
        >
        <span class="text-sm text-text-tertiary">Average hourly load</span>
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-2xl font-semibold">{{ avgDowntimeDisplay }}</span>
        <span class="text-sm text-text-tertiary">Avg downtime</span>
      </div>
    </div>
  </div>
</template>
